/** What the IndexedDB layer (store.ts) remembers of each file it keeps, without the bytes. */
export interface CachedMeta {
  /** The file's path under the game's base URL (its key in the assets manifest). */
  path: string;
  /** Its content hash, as the assets manifest gave it when it was stored. */
  hash: string;
  size: number;
  /** When it was last read or stored (ms since the epoch): the least recently used goes first. */
  used: number;
}

/**
 * The files to drop, least recently used first, so that `incoming` more bytes fit within `budget`
 * bytes in all. The file being replaced (`replacing`) counts as gone already. Null when it cannot
 * fit even with everything else dropped.
 */
export function evictionPlan(
  entries: readonly CachedMeta[],
  budget: number,
  incoming: number,
  replacing?: string,
): string[] | null {
  if (incoming > budget) return null;
  const others = entries.filter((e) => e.path !== replacing);
  let total = others.reduce((a, e) => a + e.size, 0);
  const drop: string[] = [];
  for (const e of [...others].sort((a, b) => a.used - b.used || (a.path < b.path ? -1 : 1))) {
    if (total + incoming <= budget) break;
    drop.push(e.path);
    total -= e.size;
  }
  return total + incoming <= budget ? drop : null;
}

/** Megabytes, for the player (one decimal under 10). */
export const megabytes = (bytes: number): string => {
  const mb = bytes / 1e6;
  return mb < 10 ? mb.toFixed(1) : String(Math.round(mb));
};

/**
 * The paths kept that a manifest no longer lists as they are kept: gone from it, listed with another
 * hash (an older version), or listed without a usable entry.
 */
export function stalePaths(
  kept: readonly Pick<CachedMeta, 'path' | 'hash'>[],
  files: Readonly<Record<string, { hash?: unknown } | null | undefined>>,
): string[] {
  return kept.flatMap((m) => {
    const entry = Object.hasOwn(files, m.path) ? files[m.path] : undefined;
    return entry && typeof entry === 'object' && entry.hash === m.hash ? [] : [m.path];
  });
}
